import { Plus } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

// Agent roles for NodeAgent system
const AGENT_ROLES = [
  {
    id: 'general',
    name: 'General Assistant',
    description: 'All-purpose agent for various tasks and analysis',
    capabilities: ['analyze', 'assist', 'generate', 'recommend'],
    defaultTasks: ['Document review', 'Content analysis', 'General assistance']
  },
  {
    id: 'reviewer',
    name: 'Content Reviewer',
    description: 'Specialized in reviewing and improving content quality',
    capabilities: ['review', 'edit', 'improve', 'quality-check'],
    defaultTasks: ['Content review', 'Quality assessment', 'Improvement suggestions']
  },
  {
    id: 'optimizer',
    name: 'Performance Optimizer',
    description: 'Focuses on optimizing processes and performance',
    capabilities: ['optimize', 'analyze', 'improve', 'efficiency'],
    defaultTasks: ['Performance analysis', 'Optimization recommendations', 'Process improvement']
  },
  {
    id: 'documenter',
    name: 'Documentation Specialist',
    description: 'Creates and maintains comprehensive documentation',
    capabilities: ['document', 'organize', 'structure', 'maintain'],
    defaultTasks: ['Documentation creation', 'Content organization', 'Knowledge management']
  },
  {
    id: 'security_analyst',
    name: 'Security Analyst',
    description: 'Analyzes security aspects and identifies vulnerabilities',
    capabilities: ['security-scan', 'vulnerability-check', 'compliance', 'risk-assessment'],
    defaultTasks: ['Security analysis', 'Vulnerability assessment', 'Compliance checking']
  },
  {
    id: 'data_analyst',
    name: 'Data Analyst',
    description: 'Analyzes data patterns and provides insights',
    capabilities: ['data-analysis', 'pattern-recognition', 'insights', 'visualization'],
    defaultTasks: ['Data analysis', 'Pattern detection', 'Insight generation']
  }
];

interface CustomAgentCreationPanelProps {
  onCreateAgent: (agentData: {
    name: string;
    role: string;
    description: string;
    instructions?: string;
    observedNodes?: string[];
  }) => void;
  className?: string;
}

/**
 * Component for creating custom agents with different options
 */
const CustomAgentCreationPanel: React.FC<CustomAgentCreationPanelProps> = ({
  onCreateAgent,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState('predefined');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [customName, setCustomName] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  const handleCreateFromRole = (roleId: string) => {
    const role = AGENT_ROLES.find(r => r.id === roleId);
    if (role) {
      onCreateAgent({
        name: role.name,
        role: roleId,
        description: role.description,
        instructions: `You are a ${role.name}. ${role.description}. Your primary tasks include: ${role.defaultTasks.join(', ')}.`
      });
    }
  };

  const handleCreateCustomAgent = () => {
    if (!selectedRoleId || !customName.trim()) {
      alert('Please select a role and provide a name for your agent');
      return;
    }

    const role = AGENT_ROLES.find(r => r.id === selectedRoleId);
    if (role) {
      onCreateAgent({
        name: customName,
        role: selectedRoleId,
        description: `Custom ${role.name} with specialized instructions`,
        instructions: customInstructions || `You are a custom ${role.name}. ${role.description}. ${customInstructions ? `Additional instructions: ${customInstructions}` : ''}`
      });

      // Reset form
      setCustomName('');
      setCustomInstructions('');
      setSelectedRoleId('');
    }
  };

  return (
    <div className={className}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="predefined">Predefined Roles</TabsTrigger>
          <TabsTrigger value="custom">Custom Agent</TabsTrigger>
        </TabsList>
        
        <TabsContent value="predefined">
          <div className="grid grid-cols-2 gap-4">            {AGENT_ROLES.map(role => (
              <Card key={role.id} className="overflow-hidden">
                <CardHeader className="bg-green-50 pb-2" hasCardAction={false}>
                  <CardTitle className="text-sm">{role.name}</CardTitle>
                  <CardDescription className="text-xs truncate">{role.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-3 pb-2">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {role.capabilities.slice(0, 3).map((cap, i) => (
                      <span key={i} className="text-xs bg-green-100 text-green-800 px-1 rounded">
                        {cap}
                      </span>
                    ))}
                    {role.capabilities.length > 3 && (
                      <span className="text-xs text-gray-500">+{role.capabilities.length - 3} more</span>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="pt-0 flex justify-between">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-xs"
                    onClick={() => handleCreateFromRole(role.id)}
                  >
                    Create Agent
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-xs"
                    onClick={() => {
                      setSelectedRoleId(role.id);
                      setActiveTab('custom');
                    }}
                  >
                    Customize
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </TabsContent>
          <TabsContent value="custom">
          <Card>
            <CardHeader className="bg-white" hasCardAction={false}>
              <CardTitle className="text-base">Create Custom Agent</CardTitle>
              <CardDescription>
                Customize an agent based on your specific needs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="role-base">Base Role</Label>
                <select
                  id="role-base"
                  title="Select base role for agent"
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select a base role</option>
                  {AGENT_ROLES.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="custom-name">Agent Name</Label>
                <Input
                  id="custom-name"
                  placeholder="e.g., Marketing Assistant, Code Reviewer, Security Monitor"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="custom-instructions">Custom Instructions</Label>
                <Textarea
                  id="custom-instructions"
                  placeholder="Provide specific instructions for this agent..."
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  These instructions will be added to the agent's base role capabilities
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                onClick={handleCreateCustomAgent}
                disabled={!selectedRoleId || !customName.trim()}
              >
                <Plus size={16} className="mr-2" />
                Create Custom Agent
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomAgentCreationPanel;